import { PROVIDER_LABELS } from "../../lib/models";
import { useDefaultModel } from "../../hooks/useDefaultModel";
import { useAvailableModels, type LiveModel } from "../../hooks/useAvailableModels";

/**
 * Assistant settings, currently one: which model the assistant opens with.
 *
 * Saved per user alongside every other preference, so it follows the person rather
 * than the browser, and applies to the home-page modal as well as /assistant —
 * "default" means the whole app, not this tab.
 */
export function SettingsSidebar() {
  const { models } = useAvailableModels();
  const { defaultModel, setDefaultModel, isReady, isSaving, workerDefault } = useDefaultModel();

  const groups = groupByProvider(models);

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
      <div>
        <label htmlFor="default-model" className="block text-xs font-medium text-maven-gray-800 dark:text-maven-gray-100">
          Default model
        </label>
        <p className="mt-1 text-[11px] leading-snug text-maven-gray-500 dark:text-maven-gray-500">
          Used when you open the assistant, here and from the home page.
        </p>

        <select
          id="default-model"
          value={defaultModel ?? ""}
          onChange={(e) => setDefaultModel(e.target.value)}
          disabled={!isReady || isSaving}
          className="mt-2 w-full rounded-md border border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 px-2 py-1.5 text-xs text-maven-gray-800 dark:text-maven-gray-100 focus:border-maven-teal-500 focus:outline-none focus:ring-1 focus:ring-maven-teal-500 disabled:opacity-50"
        >
          {groups.map(({ provider, models: group }) => (
            <optgroup key={provider} label={PROVIDER_LABELS[provider]}>
              {group.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {m.id === workerDefault ? " (system default)" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {isSaving && <p className="mt-1 text-[11px] text-maven-gray-500 dark:text-maven-gray-500">Saving…</p>}
        {!isReady && (
          <p className="mt-1 text-[11px] text-maven-orange-700">
            Sign-in still loading — the setting will save once your profile is available.
          </p>
        )}
      </div>

      <div className="border-t border-maven-gray-100 dark:border-maven-gray-800 pt-3">
        <p className="text-[11px] leading-snug text-maven-gray-500 dark:text-maven-gray-500">
          Changing this does not affect a conversation already in progress. Start a new
          chat, or pick a model per column, to switch mid-session.
        </p>
      </div>
    </div>
  );
}

function groupByProvider(models: LiveModel[]) {
  const order: LiveModel["provider"][] = [];
  for (const m of models) if (!order.includes(m.provider)) order.push(m.provider);
  return order.map((provider) => ({ provider, models: models.filter((m) => m.provider === provider) }));
}
